import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { uploadMedia } from "@/services/media";
import { MAX_MB, prepareJobPhoto } from "@/lib/jobMedia";
import { jobMediaFailText } from "@/lib/failText";
import { useAuth } from "@/contexts/AuthContext";
import { ReadOnlyBanner } from "@/components/ReadOnly";
import { useWakeLock } from "@/lib/wakeLock";
import { countTabletPhoto, fetchStationRequests, takeTabletRequest, type CameraRequest } from "@/services/cameraRequests";
import { mediaRun, withdrawMediaRun } from "@/services/preparation";

// His "📱 Poste photo" on the tablet (renderStation / stShot / strec): the PC's requests of the last 30
// minutes, each with when it was asked and how many photos were sent; "📸 Take photo" opens this
// tablet's own camera and sends the photo straight to the job (or its line); a Receiving request opens
// the Receiving tab to photograph the pages there. The screen stays awake while the station is on.
export default function PhotoStation({ onLeave, onReceiving }: { onLeave: () => void; onReceiving: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["camera-station"], queryFn: fetchStationRequests, refetchInterval: 5000 });
  const input = useRef<HTMLInputElement>(null);
  const [target, setTarget] = useState<CameraRequest | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = window.setInterval(() => setNow(Date.now()), 30_000); return () => window.clearInterval(id); }, []);

  // His wake lock: a tablet left on the station does not go to sleep — taken again each time the page
  // comes back, since the native camera hides the page and the browser drops the lock then.
  useWakeLock(true, true, true);
  // Only the requests this person can answer: a job's photos need the Montage, a delivery note the
  // Receiving — and neither when the module is read-only for them.
  const { staff } = useAuth();
  const readOnly = (module: string) => !!staff?.read_only_modules?.includes(module);
  const can = (module: string) => (staff?.modules?.includes(module) ?? true) && !readOnly(module);
  const moduleOf = (r: CameraRequest) => (r.mode === "rec" ? "receiving" : "preparation");

  const send = async (file: File | undefined) => {
    const r = target;
    setTarget(null);
    if (!r || !file || (!r.run_id && r.myotaku_event_id == null)) return;
    setBusy(r.id);
    setError(null);
    let made: number | null = null;
    try {
      const photo = await prepareJobPhoto(file);
      // A job not started was asked for by its planning entry: its first photo makes the run — in the list
      // the PC showed — before it goes up to it, as a photo taken on the PC before the start does; never the
      // request itself (his camTablet makes nothing). The count then names that run on the request.
      const run = r.run_id ?? (made = (await mediaRun(r.myotaku_event_id!, r.from_orders ?? undefined)).run_id);
      await uploadMedia("run", run, photo, undefined, r.item_key);
      made = null;
      // His `stDone`: the photo is the job's once it is up; a count that cannot be written is only logged.
      await countTabletPhoto(r.id).catch(() => {});
      void qc.invalidateQueries({ queryKey: ["camera-station"] });
    } catch (e) {
      // His `stDone` goes through `addMedia`: his sentence, never a status (MediaStrip's catch). A run made
      // for this photo holds nothing and is taken back, as the strip does.
      setError(jobMediaFailText(t, e, { video: false, mb: Math.ceil(file.size / 1_048_576), max: MAX_MB.image }));
      if (made != null) await withdrawMediaRun(r.myotaku_event_id!, made).catch(() => {});
    } finally {
      setBusy(null);
    }
  };

  const open = (list.data?.requests ?? []).filter((r) => can(moduleOf(r)));
  // A request hidden only because this person reads that module: his read-only banner says why,
  // never "No request yet" while the PC waits for the tablet.
  const roWaiting = (list.data?.requests ?? []).some((r) => readOnly(moduleOf(r)));
  const ago = (iso: string) => { const m = Math.floor((now - new Date(iso).getTime()) / 60000); return m < 1 ? t("st.agoNow") : t("st.ago", { m }); };
  return (
    <section aria-label={t("st.title")} className="fixed inset-0 z-40 flex flex-col gap-4 overflow-auto bg-slate-50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold text-slate-900">{t("st.title")}</h2>
        <button onClick={onLeave} className="rounded-lg px-3 py-2 text-sm ring-1 ring-slate-300">{t("st.quit")}</button>
      </div>
      <p className="text-sm text-slate-600">{t("st.hint")}</p>
      {/* His sentence whenever Assembly is read-only; Receiving read-only alone (a mix only per-module
          access makes) says only that, never that timers cannot be started. */}
      <ReadOnlyBanner module={readOnly("preparation") ? "preparation" : "receiving"} text={t(readOnly("preparation") ? "job.readOnly" : "recv.readOnly")} />
      {error && <p className="text-sm text-red-700">{error}</p>}
      {open.length === 0 ? (
        !roWaiting && <p className="rounded-xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-200">{t("st.none")}</p>
      ) : (
        <ul className="space-y-3">
          {open.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-white p-4 ring-1 ring-slate-200">
              <span className="min-w-0 flex-1">
                <b className="block text-slate-900">{r.mode === "rec" ? t("st.recL") : r.label}</b>
                <span className="text-xs text-slate-500">{ago(r.at)}{r.photo_count > 0 ? ` · ${t(r.photo_count > 1 ? "st.sentMany" : "st.sentOne", { n: r.photo_count })}` : ""}</span>
              </span>
              {r.mode === "rec" ? (
                <button onClick={() => { void takeTabletRequest(r.id).catch(() => {}); onReceiving(); }}
                  className="rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white">{t("st.rec")}</button>
              ) : (
                <button onClick={() => { setTarget(r); input.current?.click(); }} disabled={busy !== null}
                  className="rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">{busy === r.id ? t("st.up") : t("st.shot")}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      <input ref={input} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void send(e.target.files?.[0]); e.target.value = ""; }} />
    </section>
  );
}
